"use client";

import Image from 'next/image';
import Link from 'next/link';
import banner from '../../public/images/career/banner.png'
import { MdOutlineNavigateNext } from "react-icons/md";
import { useEffect, useRef, useState } from 'react';
import { m, useScroll, useTransform } from 'framer-motion';
import CareerAppliedModal from './CareerAppliedModal';
import { CoreCommitment as Icon_CoreCommitment } from "@/components/customer/icons/CoreCommitment";
import { CoreGrowth as Icon_CoreGrowth } from "@/components/customer/icons/CoreGrowth";
import { CoreOwnership as Icon_CoreOwnership } from "@/components/customer/icons/CoreOwnership";
import { CorePositivity as Icon_CorePositivity } from "@/components/customer/icons/CorePositivity";
import { CoreTeamwork as Icon_CoreTeamwork } from "@/components/customer/icons/CoreTeamwork";
import { CoreValueInnovation as Icon_CoreValueInnovation } from "@/components/customer/icons/CoreValueInnovation";
const SvgIcons = { CoreCommitment: Icon_CoreCommitment, CoreGrowth: Icon_CoreGrowth, CoreOwnership: Icon_CoreOwnership, CorePositivity: Icon_CorePositivity, CoreTeamwork: Icon_CoreTeamwork, CoreValueInnovation: Icon_CoreValueInnovation };
import { getImageUrl } from "@/lib/imageUrl";

const CareerPage = ({ initialCareers = [] }: { initialCareers?: any[] }) => {

    interface Job {
        description: string;
        id: number;
        career_role_id: number;
        title: string;
        slug: string;
        location: string;
        employment_type: string;
        salary_range: string;
        job_description: string;
        requirements: string;
        posted_at: string;
        closing_date: string;
        created_at: string;
        updated_at: string;
        status: string;
    }

    interface Career {
        id: number;
        name: string;
        slug: string;
        description: string;
        status: string;
        jobs: Job[];
    }

    const [careers, setCareers] = useState<Career[]>(initialCareers);
    const [selectedCategory, setSelectedCategory] = useState<string>('All');
    const [showAppliedBanner, setShowAppliedBanner] = useState(false);

    // "Posted 3 days ago" style — falls back to a plain date once it's old
    // enough that a relative label stops being useful at a glance.
    const formatPostedDate = (dateStr?: string) => {
        if (!dateStr) return null;
        const posted = new Date(dateStr);
        if (Number.isNaN(posted.getTime())) return null;

        const days = Math.floor((Date.now() - posted.getTime()) / (1000 * 60 * 60 * 24));
        if (days <= 0) return 'Posted today';
        if (days === 1) return 'Posted 1 day ago';
        if (days < 30) return `Posted ${days} days ago`;

        return `Posted ${posted.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}`;
    };

    // Read the query param directly instead of useSearchParams() — that hook
    // requires wrapping this whole page in a Suspense boundary, which it
    // isn't. window.location.search works fine here since this only needs to
    // run once on mount in the browser, not during SSR.
    useEffect(() => {
        if (window.location.search.includes("applied=success")) {
            setShowAppliedBanner(true);
            const url = new URL(window.location.href);
            url.searchParams.delete("applied");
            window.history.replaceState({}, "", url.toString());
        }
    }, []);

    // Same static JSON snapshot pattern as blogs.json/pages.json (see
    // BlogOverviewWrapper.tsx) — regenerated by growsuite whenever a role or
    // job posting changes (CMSController::generateCareersJson()), instead of
    // hitting the encrypted /careers API on every page load.
    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await fetch(getImageUrl("/vilvabusiness/careers.json"), {
                    next: { revalidate: 60 },
                });
                const parsedData = await response.json();
                setCareers(parsedData.careers ?? []);
            } catch (error) {
                console.error("Error fetching careers:", error);
            }
        };

        fetchData();
    }, []);

    //   const handleJobClick = (jobTitle) => {
    //     const encodedJobTitle = encodeURIComponent(jobTitle);
    //     router.push(`/career/encodedJobTitle?jobTitle=${id}`);
    //   };
    const openingRolesRef = useRef<HTMLDivElement | null>(null);
    // Banner zooms in as it scrolls through the middle of the viewport, then
    // back out as it scrolls past — a Ken Burns-style scroll-linked effect.
    const bannerRef = useRef<HTMLDivElement | null>(null);
    const { scrollYProgress: bannerScrollProgress } = useScroll({
        target: bannerRef,
        offset: ['start end', 'end start'],
    });
    const bannerScale = useTransform(bannerScrollProgress, [0, 0.5, 1], [0.85, 1.05, 0.85]);

    const handleScrollToJobs = () => {
        if (openingRolesRef.current) {
            openingRolesRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    };
    const totalJobs = careers.reduce((sum, career) => sum + career.jobs.length, 0);


    return (<>
        <div className="bg-white dark:bg-[#0B1120] text-[#2C2845] dark:text-[#E5E7EB]">
            <CareerAppliedModal
                open={showAppliedBanner}
                onClose={() => setShowAppliedBanner(false)}
                onViewMoreJobs={() => {
                    setShowAppliedBanner(false);
                    handleScrollToJobs();
                }}
            />
            <section>
                <div className='sm:container container-none md:px-[0px] px-[16px] flex flex-col justify-center'>
                    <div className='flex flex-col justify-center  my-[30px]'>
                        <div className="flex justify-center flex-col items-center mx-auto  space-y-[24px]">
                            <div className=' flex flex-col justify-center items-center'>
                                <p className='text-center'> <span className='text-[#195AFF] mb-[8px] text-[14px] font-LatinkaRegular font-[500]'>{totalJobs}</span> Jobs - <span className='text-[#195AFF] font-[500] text-[14px]'>2</span> Locations</p>
                                <h1 className='text-[40px] md:text-[60px] mb-[2px] leading-none font-NeueMontreal font-bold text-[#090D28] !w-fit text-center '>
                                    Start with a Goal.
                                </h1>
                                <h2 className='text-[40px] md:text-[60px] text-center leading-none font-NeueMontreal font-bold text-[#0067EE] !w-fit '>
                                    Make a Difference.
                                </h2>
                            </div>
                            <div className='w-full'>
                                <p className='text-[#2C2845] text-[16px] md:text-[18px] max-w-4xl font-Schibsted_Grotesk text-center '>
                                    {/* Was product-marketing copy lifted almost verbatim from the D2C
                                        page — it told a candidate nothing about working here. This
                                        version is deliberately limited to things that are true and
                                        already public elsewhere on the site: the Chennai base, the
                                        products, the team size. No perks or benefits are claimed,
                                        because none are documented anywhere in the codebase. */}
                                    We&apos;re a small, sharp team in Chennai building the tools Indian businesses actually run on — CRM and HRM, WhatsApp automation, storefronts and payments. You&apos;ll own real problems from your first week, ship work that reaches customers in weeks rather than quarters, and learn fast alongside people who care about the craft.
                                </p>
                            </div>
                            <div className='text-center'>
                                <button onClick={handleScrollToJobs} className='bg-[#195AFF] text-center py-[16px] px-[32px] rounded-[8px] md:text-[20px] text-[18px] text-white  font-Schibsted_Grotesk'>
                                    Explore all jobs
                                </button>
                            </div>
                        </div>

                        <div ref={bannerRef} className='flex justify-center items-center my-[60px] overflow-hidden'>
                            {/* Desktop Image */}
                            <m.div style={{ scale: bannerScale }}>
                                <Image
                                    src={getImageUrl("/vilvabusiness/website/career/banner_career_2-Desktop.svg")}
                                    alt="Career banner"
                                    width={580}
                                    height={382}
                                    quality={60}
                                    sizes="(max-width: 768px) 100vw, 880px"
                                    className="w-[1000px] h-auto"
                                />
                            </m.div>
                        </div>

                        <div ref={openingRolesRef} className='flex !justify-center md:mx-auto mx-0 flex-col w-full '>
                            <h2 className='text-[#090D28] font-bold md:text-[32px] text-[28px] text-center font-NeueMontreal mb-[24px]'>Current Opening Roles</h2>

                            {/* Category filter — "All" plus one per career category, each showing its vacancy count */}
                            <div className="flex flex-wrap justify-center gap-2 mb-[32px]">
                                {[
                                    { name: 'All', count: totalJobs },
                                    ...careers
                                        .filter((c) => (c.jobs?.length ?? 0) > 0)
                                        .map((c) => ({ name: c.name, count: c.jobs.length })),
                                ].map(({ name, count }) => (
                                    <button
                                        key={name}
                                        type="button"
                                        onClick={() => setSelectedCategory(name)}
                                        className={`px-4 py-2 rounded-full text-[13px] sm:text-[14px] font-Schibsted_Grotesk font-semibold transition-colors ${selectedCategory === name
                                            ? 'bg-[#195AFF] text-white'
                                            : 'bg-[#F0F4FF] text-[#195AFF] hover:bg-[#E6EDFF]'
                                            }`}
                                    >
                                        {name} <span className="opacity-70">({count})</span>
                                    </button>
                                ))}
                            </div>

                            <div className="max-w-7xl mx-auto w-full">
                                <div>
                                    {careers.length > 0 ? (
                                        <div className="w-full grid grid-cols-1 sm:grid-cols-2 gap-2">
                                            {careers
                                                .filter((career) => selectedCategory === 'All' || career.name === selectedCategory)
                                                .flatMap((career) => career.jobs || [])
                                                .map((job, index) => {
                                                return (
                                                    <m.div
                                                        key={job.id}
                                                        initial={{ opacity: 0, y: 24 }}
                                                        whileInView={{ opacity: 1, y: 0 }}
                                                        viewport={{ once: true, amount: 0.2 }}
                                                        transition={{ duration: 0.4, delay: (index % 6) * 0.06, ease: 'easeOut' }}
                                                        whileHover={{ y: -4 }}
                                                        className='relative mb-[10px] flex flex-col p-3 sm:p-[12px] border-2 border-[#F5F5F5] rounded-[8px] w-full cursor-pointer transition-shadow hover:shadow-[0px_8px_24px_rgba(25,90,255,0.10)]'
                                                    >
                                                        <div className='flex items-center justify-between w-full'>
                                                            <div className="mb-2 sm:mb-0 min-w-0">
                                                                <p className="first_value truncate text-[#090D28] font-semibold text-[16px] sm:text-[18px] font-Schibsted_Grotesk">
                                                                    {job.title}
                                                                </p>
                                                                <p className="text-[#090D28] text-[12px] sm:text-[14px] font-Schibsted_Grotesk">
                                                                    Location: {job.location || 'Chennai'}
                                                                </p>
                                                            </div>

                                                            <div>
                                                                <Link
                                                                    href={`/careers/${job.slug}`}
                                                                    aria-label={`View the ${job.title} role`}
                                                                    className="focus:outline-none space-x-2 border-2 flex justify-center items-center border-[#195AFF] h-[40px] w-[40px] rounded-full hover:bg-[#FFF] bg-[#195AFF] group transition-colors duration-200"
                                                                >
                                                                    <MdOutlineNavigateNext size={20} className="text-[#FFF] group-hover:text-[#195AFF] transition-colors duration-200" />
                                                                </Link>
                                                            </div>
                                                        </div>

                                                        {/* Salary + employment type */}
                                                        <div className="flex flex-wrap items-center gap-2 mt-2">
                                                            {job.employment_type && (
                                                                <span className="px-2.5 py-1 bg-[#E6EDFF] text-[#195AFF] rounded-full text-[11px] font-Schibsted_Grotesk font-semibold">
                                                                    {job.employment_type}
                                                                </span>
                                                            )}
                                                            {job.salary_range && (
                                                                <span className="px-2.5 py-1 bg-[#F5F5F5] text-[#393333] rounded-full text-[11px] font-Schibsted_Grotesk font-semibold">
                                                                    {job.salary_range}
                                                                </span>
                                                            )}
                                                        </div>

                                                        {formatPostedDate(job.posted_at || job.created_at) && (
                                                            <div className="flex items-center gap-1 mt-2 text-[#70708D]">
                                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                                                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                                                                </svg>
                                                                <span className="text-[11px] font-Schibsted_Grotesk">
                                                                    {formatPostedDate(job.posted_at || job.created_at)}
                                                                </span>
                                                            </div>
                                                        )}

                                                        <div className='w-full h-[2px] bg-[#F5F5F5] my-[12px]'></div>

                                                        <div>
                                                            <p className='text-[#000000]/60 font-Schibsted_Grotesk text-[14px] font-[400]'>
                                                                {(() => {
                                                                    const fullText =
                                                                        job.description?.trim() ||
                                                                        job.job_description?.trim() ||
                                                                        '';

                                                                    if (!fullText) {
                                                                        return 'View the full role description and requirements.';
                                                                    }

                                                                    const firstSentence = fullText.split('.')[0];

                                                                    return firstSentence ? firstSentence + '.' : fullText;
                                                                })()}
                                                            </p>
                                                        </div>
                                                    </m.div>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <div className="flex justify-center items-center min-h-[300px]">
                                            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#1D31C3]"></div>
                                            <span className="ml-4 text-[#1D31C3] font-semibold">Loading careers...</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                </div>

                {/* <div className='my-[40px] mx-auto container flex justify-center'>

                    <div className='md:flex block items-center justify-between md:space-x-[30px] space-x-[0px]  lg:w-[70vw] md:w-full'>
                        <div className='md:w-7/12 w-full  md:space-y-[10px] space-y-[0px] '>
                            <h2 className='text-[40px] !w-fit  text-start leading-none font-LatinkaRegular font-bold !bg-gradient-to-r !from-[#000985] !to-[#3345C7] !bg-clip-text !text-transparent'>Our Mission</h2>
                            <p className='text-[#2C2845] text-[18px] font-LatinkaRegular text-start leading-relaxed'>
                                Our mission is to empower businesses of all sizes with innovative, reliable, and cutting-edge technology solutions that make a real difference.
                                We believe that every company deserves tools that are not only powerful but also intuitive and scalable, helping them focus on what they do best — growing their business and serving their customers.

                                At Vilva Business, we are committed to delivering exceptional service and unmatched support at every step. From the very first consultation to long-term partnership, our team works tirelessly to understand your unique challenges and goals, crafting tailored solutions that drive measurable growth and lasting success.

                                We thrive on solving complex problems and pushing boundaries. Whether it’s streamlining e-commerce operations, automating workflows, building custom SaaS platforms, or implementing secure and scalable cloud infrastructure, we embrace every challenge as an opportunity to innovate and deliver results that exceed expectations.

                                Quality is at the heart of everything we do. Every line of code, every design decision, and every client interaction reflects our unwavering dedication to excellence. We combine deep technical expertise with a genuine passion for helping businesses succeed in an ever-evolving digital landscape.

                                Our growing team is made up of talented developers, designers, strategists, and customer success specialists who share a common vision: to be the most trusted technology partner for ambitious companies around the world. We celebrate diversity, encourage continuous learning, and foster a collaborative environment where ideas flourish and innovation never stops.

                                When you choose Vilva Business, you’re not just getting software — you’re gaining a dedicated partner that is fully invested in your success. We measure our achievements by the growth of our clients and the strength of the relationships we build along the way.

                                Join us on this exciting journey as we continue to shape the future of business technology, one successful project at a time.
                            </p>
                        </div>
                        <div className='md:w-5/12 flex justify-center'>
                            <Image src={two} className='fluid md:w-[880px] w-[400px]' alt='Career page image' />
                        </div>
                    </div>
                </div> */}


                <section className="bg-[#FFFFFF]">
                    <section className="px-4 md:container  py-[80px] md:py-[120px]">
                        <h2
                            data-aos="slide-up"
                            data-aos-duration="2000"
                            className="text-[#090D28] text-center font-NeueMontreal text-[32px] mb-[100px] md:text-[48px] font-normal leading-[120%]"
                        >
                            The Core Values That Drive Vilva
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[24px]">
                            <div className="core-value-card rounded-3xl bg-[#EFFCE9] p-6">
                                <SvgIcons.CoreValueInnovation />
                                <p className="text-[#309200] py-[16px] text-[22px] leading-[120%] font-[700] font-Schibsted_Grotesk ">
                                    Innovation
                                </p>
                                <p className="text-[#373844] text-[18px] leading-[150%] font-normal font-Schibsted_Grotesk">
                                    We embrace emerging technologies and creative thinking to deliver
                                    future-ready products that give our clients a competitive edge.
                                </p>
                            </div>
                            <div className="core-value-card rounded-3xl bg-[#E9F5FC] p-6">
                                <SvgIcons.CoreGrowth />
                                <p className="text-[#157FE3] py-[16px] text-[22px] leading-[120%] font-[700] font-Schibsted_Grotesk ">
                                    Growth
                                </p>
                                <p className="text-[#373844] text-[18px] leading-[150%] font-normal font-Schibsted_Grotesk">
                                    We focus on building scalable and efficient solutions that fuel real
                                    business growth and long-term success.
                                </p>
                            </div>
                            <div className="core-value-card rounded-3xl bg-[#FCF9E9] p-6">
                                <SvgIcons.CoreOwnership />
                                <p className="text-[#B67D19] py-[16px] text-[22px] leading-[120%] font-[700] font-Schibsted_Grotesk ">
                                    Ownership
                                </p>
                                <p className="text-[#373844] text-[18px] leading-[150%] font-normal font-Schibsted_Grotesk">
                                    From start to finish, we own every detail ensuring accountability,
                                    quality, and on-time delivery.
                                </p>
                            </div>
                            <div className="core-value-card rounded-3xl bg-[#FDF2F7] p-6">
                                <SvgIcons.CoreTeamwork />
                                <p className="text-[#E52F69] py-[16px] text-[20px] leading-[120%] font-[700] font-Schibsted_Grotesk ">
                                    Teamwork
                                </p>
                                <p className="text-[#373844] text-[18px] leading-[150%] font-normal font-Schibsted_Grotesk">
                                    We work hand-in-hand across roles and ideas, building synergy that
                                    leads to smarter, smoother execution.
                                </p>
                            </div>
                            <div className="core-value-card rounded-3xl bg-[#E9FCF6] p-6">
                                <SvgIcons.CoreCommitment />
                                <p className="text-[#007852] py-[16px] text-[22px] leading-[120%] font-[700] font-Schibsted_Grotesk ">
                                    Commitment
                                </p>
                                <p className="text-[#373844] text-[18px] leading-[150%] font-normal font-Schibsted_Grotesk">
                                    Our word is our bond. We prioritize reliability and consistency in
                                    every phase of the project.
                                </p>
                            </div>
                            <div className="core-value-card rounded-3xl bg-[#F3EDFC] p-6">
                                <SvgIcons.CorePositivity />
                                <p className="text-[#8245DC] py-[16px] text-[22px] leading-[120%] font-[700] font-Schibsted_Grotesk ">
                                    Positivity
                                </p>
                                <p className="text-[#373844] text-[18px] leading-[150%] font-normal font-Schibsted_Grotesk">
                                    We face challenges with optimism, fostering a supportive, high-energy
                                    environment for both team and clients.
                                </p>
                            </div>
                        </div>
                    </section>
                </section>
            </section>



            {/* <Footer/> */}

        </div>
    </>
    )
}

export default CareerPage 